import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "wouter";

export type Theme = "dark" | "light" | "system";

export const PORTAL_THEME_STORAGE_KEY = "godman-portal-theme";
const LEGACY_THEME_STORAGE_KEY = "godman-ui-theme-v3";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: "light",
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function isPortalPath(pathname: string): boolean {
  return (
    pathname.startsWith("/dashboard") ||
    pathname.startsWith("/admin") ||
    pathname.startsWith("/superadmin")
  );
}

/** Public marketing pages always use light; portal pages use stored preference. */
export function applyDocumentTheme(portalTheme: Theme, pathname: string): void {
  const root = document.documentElement;
  root.classList.remove("light", "dark");

  if (!isPortalPath(pathname)) {
    root.classList.add("light");
    return;
  }

  if (portalTheme === "system") {
    const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
    root.classList.add(systemTheme);
    return;
  }

  root.classList.add(portalTheme);
}

function readStoredPortalTheme(storageKey: string, defaultTheme: Theme): Theme {
  const stored = localStorage.getItem(storageKey) as Theme | null;
  if (stored) return stored;

  const legacy = localStorage.getItem(LEGACY_THEME_STORAGE_KEY) as Theme | null;
  if (legacy) {
    localStorage.setItem(storageKey, legacy);
    return legacy;
  }

  return defaultTheme;
}

/** Syncs `<html>` class with route — public stays light after portal dark mode + logout. */
export function RouteThemeBinder() {
  const [location] = useLocation();
  const { theme } = useTheme();

  useEffect(() => {
    applyDocumentTheme(theme, location);
  }, [theme, location]);

  return null;
}

export function ThemeProvider({
  children,
  defaultTheme = "light",
  storageKey = PORTAL_THEME_STORAGE_KEY,
  ...props
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() =>
    readStoredPortalTheme(storageKey, defaultTheme)
  );

  const value = {
    theme,
    setTheme: (next: Theme) => {
      localStorage.setItem(storageKey, next);
      setThemeState(next);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
